import type { LinkType } from '@servicetim/shared';
import { useDeleteLinkType, useLinkTypes } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  DropdownMenuSeparator,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { RowActions } from '../common/row-actions';
import { withProblemMessage } from '../common/server-errors';
import { LinkTypeDialog } from './link-type-dialog';

type Dialog = { kind: 'create' } | { kind: 'edit'; linkType: LinkType } | null;

/** System link types (relates, blocks, duplicates, clones) are read-only. */
export function LinkTypesTable() {
  const linkTypes = useLinkTypes();
  const remove = useDeleteLinkType();
  const confirm = useConfirm();
  const [dialog, setDialog] = useState<Dialog>(null);

  const onDelete = useCallback(
    (linkType: LinkType) =>
      void confirm({
        title: `Delete link type ${linkType.name}?`,
        description: 'Refused while tickets are linked with it.',
        confirmLabel: 'Delete',
        destructive: true,
        onConfirm: async () => {
          await withProblemMessage(() => remove.mutateAsync(linkType.id));
          toast.success(`${linkType.name} deleted`);
        },
      }),
    [confirm, remove.mutateAsync],
  );

  const columns = useMemo<ColumnDef<LinkType, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-fg">
            {row.original.name}
            {row.original.isSystem ? <Badge variant="outline">system</Badge> : null}
          </span>
        ),
      },
      { accessorKey: 'outward', header: 'Outward' },
      { accessorKey: 'inward', header: 'Inward' },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <RowActions label={`Actions for ${row.original.name}`}>
            <DropdownMenuItem
              disabled={row.original.isSystem}
              onSelect={() => setDialog({ kind: 'edit', linkType: row.original })}
            >
              <Pencil /> Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={row.original.isSystem}
              onSelect={() => onDelete(row.original)}
            >
              <Trash2 /> Delete
            </DropdownMenuItem>
          </RowActions>
        ),
      },
    ],
    [onDelete],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={linkTypes.data}
        isLoading={linkTypes.isPending}
        error={linkTypes.error}
        onRetry={() => void linkTypes.refetch()}
        getRowId={(l) => l.id}
        caption="Link types"
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New link type
            </Button>
          </div>
        }
        emptyTitle="No link types"
        emptyDescription="System link types are seeded on first use."
      />
      {dialog ? (
        <LinkTypeDialog
          linkType={dialog.kind === 'edit' ? dialog.linkType : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
